<template>
  <v-content class="grey lighten-4">
    <AppBar title="Tågvändningar" :extension-height="50">
      <template v-slot:extension>
        <v-container class="py-0">
          <v-form @submit.prevent>
            <v-row>
              <v-col class="py-0">
                <v-text-field
                  v-model="filter"
                  block
                  dense
                  outlined
                  hide-details
                  class="mb-3"
                  placeholder="Filtrera..."
                />
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </template>
    </AppBar>
    <AppNavigationDrawer />

    <v-container class="px-0 pt-0">
      <transition name="scroll-y-transition" mode="out-in">
        <v-row
          v-if="!loading && data.length"
          class="d-flex flex-column align-center px-4"
        >
          <v-data-table
            :headers="headers"
            :items="filteredData"
            :mobile-breakpoint="0"
            :sort-by="sortBy"
            :sort-desc="sortDesc"
            class="elevation-1"
            style="width: 100%"
            hide-default-footer
            disable-pagination
            fixed-header
            height="calc(100vh - 120px)"
          >
            <template v-slot:item="{ item }">
              <tr>
                <td colspan="3" class="px-0">
                  <div class="d-flex px-4" style="width: 100%">
                    <div
                      class="d-flex pr-4"
                      style="border-right: 1px solid rgba(0, 0 ,0, .16); width: calc(50% - 25px)"
                    >
                      <div class="d-flex flex-column">
                        <div
                          @click="
                            onClickTrip(item.fromDepartureDate, item.fromNumber)
                          "
                          style="font-weight: bold; width: 60px"
                        >
                          {{ item.fromNumber }}
                        </div>
                        <div class="caption grey--text text--darken-1">
                          {{ item.fromVehicle }}
                        </div>
                      </div>

                      <div class="d-flex flex-column">
                        <div>{{ item.fromPlace }}</div>
                        <div class="caption grey--text text--darken-1">
                          {{ item.fromTime | formatTime }}
                        </div>
                      </div>
                    </div>

                    <div class="d-flex">
                      <div
                        style="width: 50px;"
                        class="d-flex flex-column align-center"
                      >
                        <div
                          class="caption grey--text text--darken-1 pt-1"
                          style="height: 25px"
                          v-if="item.isNotSamePlace"
                        >
                          <v-icon size="16" color="red"
                            >mdi-chevron-right</v-icon
                          >
                        </div>

                        <div>
                          {{ item.vandTid }}
                        </div>
                      </div>
                    </div>

                    <div
                      @click="onClickTrip(item.toDepartureDate, item.toNumber)"
                      class="d-flex pl-4"
                      style="width: calc(50% - 25px); border-left: 1px solid rgba(0, 0 ,0, .16)"
                    >
                      <div class="d-flex flex-column">
                        <div style="width: 50px">{{ item.toPlace }}</div>
                        <div class="caption grey--text text--darken-1">
                          {{ item.toTime | formatTime }}
                        </div>
                      </div>
                      <div class="d-flex flex-column">
                        <div style="font-weight: bold;">
                          {{ item.toNumber }}
                        </div>
                        <div class="caption grey--text text--darken-1">
                          {{ item.toVehicle }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    class="d-flex"
                    v-if="item.fromHasActivities || item.toHasActivities"
                  >
                    <div
                      style="width: calc(50% - 25px); border-right: 1px solid rgba(0, 0 ,0, .16)"
                      class="pl-4"
                    >
                      <ActivtyRow
                        hide-text
                        :activities="item.fromActivities"
                        type="after"
                      />
                    </div>
                    <div style="width: 50px; height: 24px"></div>
                    <div
                      class="pl-4"
                      style="width: calc(50% - 25px); border-left: 1px solid rgba(0, 0 ,0, .16)"
                    >
                      <ActivtyRow
                        hide-text
                        :activities="item.toActivities"
                        type="before"
                      />
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-row>
        <v-row
          no-gutters
          v-else-if="!loading && !data.length"
          class="d-flex flex-column align-center pt-12"
        >
          <v-icon size="50">mdi-information-outline</v-icon>
          <span class="grey--text text--darken-1 mt-4"
            >Ingen data hittades</span
          >
        </v-row>
        <v-row no-gutters v-else class="d-flex flex-column align-center pt-12">
          <div class="d-flex align-center">
            <v-progress-circular
              indeterminate
              color="primary"
            ></v-progress-circular>
            <span class="grey--text text--darken-1 ml-4">Hämtar data...</span>
          </div>
        </v-row>
      </transition>
    </v-container>
  </v-content>
</template>

<script>
import AppBar from '@/components/AppBar'
import AppNavigationDrawer from '@/components/AppNavigationDrawer'
import ActivtyRow from '@/components/ActivtyRow'
import axios from 'axios'
import { get } from 'vuex-pathify'
import { format } from 'date-fns'
import orderby from 'lodash.orderby'

export default {
  name: 'TrainReversals',

  components: {
    AppBar,
    AppNavigationDrawer,
    ActivtyRow
  },

  data: () => ({
    sortBy: 'fromTime',
    sortDesc: false,
    loading: true,
    data: [],
    filter: '',
    headers: [
      { text: 'Ank.tågnr.', value: 'fromNumber' },

      { text: '', value: 'vandTid', width: 50 },

      { text: 'Avg.tågnr.', value: 'toNumber' }
    ]
  }),

  created() {
    this.fetch()
  },

  watch: {
    districtId() {
      this.fetch()
    }
  },

  computed: {
    districtId: get('sso/districtId'),

    filteredData() {
      return this.data.filter(x => {
        const filter = this.filter.toLowerCase()
        return (
          !filter.length ||
          x.fromToVehicle.toLowerCase().includes(filter) ||
          x.fromToNumber.toLowerCase().includes(filter) ||
          x.fromToTime.toLowerCase().includes(filter) ||
          x.fromToPlace.toLowerCase().includes(filter)
        )
      })
    }
  },

  filters: {
    formatTime(value) {
      if (!value) return

      return format(new Date(value), 'HH:mm')
    }
  },

  methods: {
    onClickTrip(departureDate, number) {
      if (!departureDate || !number) return
      this.$router.push(`/crew/${departureDate}/${number}`)
    },

    async fetch() {
      this.loading = true
      const { data } = await axios.get(
        `https://arriva-integration-platform.reges.io/arriva/kalender-avgangar/1?district=${this.districtId}`,
        {
          headers: {
            Authorization: `Basic c3ZjLXRyYWluZC1hcnJpdmEtMDFAcmVnZXMuc2U6QT04L2RjfWhlOkJWYCRUdA==`
          }
        }
      )

      this.data = Object.values(
        data.reduce((acc, item) => {
          if (item.vehicle === 'Unplanned') return acc
          if (!acc[item.vehicle]) {
            acc[item.vehicle] = []
          }
          acc[item.vehicle].push(item)
          return acc
        }, {})
      ).flatMap(x => {
        const circulation = x.sort(
          (a, b) => +new Date(a.timeStart) - +new Date(b.timeStart)
        )

        const getVandTid = (fromTime, toTime) => {
          if (!fromTime || !toTime) return ''

          const diffMs = +new Date(toTime) - +new Date(fromTime)

          const hours = Math.floor(diffMs / 60000 / 60)
          const min = (diffMs / 60000) % 60
          if (!hours) {
            return `${min}m`
          }
          return `${hours}h${min}`
        }

        const getIsNotSamePlace = (fromPlace, toPlace) => {
          if (!fromPlace || !toPlace) return ''

          if (
            (fromPlace === 'FGN' && toPlace === 'FGC') ||
            (toPlace === 'FGN' && fromPlace === 'FGC')
          )
            return false

          return fromPlace !== toPlace
        }

        const makeItem = (fromItem, toItem) => {
          const fromVehicle = fromItem?.vehicle
            ? `${fromItem.vehicle}${
                fromItem.position ? ` M${fromItem.position}` : ''
              }`
            : ''
          const toVehicle = toItem?.vehicle
            ? `${toItem.vehicle}${
                toItem.position ? ` M${toItem.position}` : ''
              }`
            : ''
          const fromToVehicle = `${fromVehicle}-${toVehicle}`
          const fromNumber = fromItem?.number || ''
          const toNumber = toItem?.number || ''
          const fromToNumber = `${fromNumber}-${toNumber}`
          const fromTime = fromItem?.timeEnd || ''
          const toTime = toItem?.timeStart || ''
          const fromToTime = `${fromTime}-${toTime}`
          const fromPlace = fromItem?.placeEnd || ''
          const toPlace = toItem?.placeStart || ''
          const fromToPlace = `${fromPlace}-${toPlace}`
          const fromActivities = fromItem?.activities || []
          const toActivities = toItem?.activities || []
          const isNotSamePlace = getIsNotSamePlace(fromPlace, toPlace)
          const vandTid = getVandTid(fromTime, toTime)
          const fromDepartureDate = fromItem?.departureDate || ''
          const toDepartureDate = toItem?.departureDate || ''
          const fromHasActivities = !!fromItem?.activities?.filter(x =>
            [null, 1].includes(x.position)
          ).length
          const toHasActivities = !!toItem?.activities?.filter(x =>
            [0].includes(x.position)
          ).length

          return {
            fromVehicle,
            toVehicle,
            fromToVehicle,
            fromNumber,
            toNumber,
            fromToNumber,
            fromTime,
            toTime,
            fromToTime,
            fromPlace,
            toPlace,
            fromToPlace,
            fromActivities,
            toActivities,
            isNotSamePlace,
            vandTid,
            fromDepartureDate,
            toDepartureDate,
            fromHasActivities,
            toHasActivities
          }
        }

        const items = []

        for (let i = -1; i < circulation.length; i++) {
          const item = circulation[i]
          const nextItem = circulation[i + 1]
          items.push(makeItem(item, nextItem))
        }

        return items
      })

      this.data = orderby(this.data, ['fromVehicle'], ['asc'])

      this.loading = false
    }
  }
}
</script>
